import { useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ChevronDown, RefreshCw } from "lucide-react";
import { ApiError } from "@/lib/api.ts";
import { cn } from "@/lib/utils.ts";
import { Button } from "@/components/ui/button.tsx";
import { CopyButton } from "./copy-button.tsx";

export interface RouteErrorPageProps {
  error: unknown;
  reset?: () => void;
  info?: { componentStack?: string };
}

function describe(error: unknown): { title: string; message: string } {
  if (error instanceof ApiError) {
    if (error.status === 403) return { title: "You don't have access", message: "Your account isn't allowed to view this page. Ask an administrator if you think that's wrong." };
    if (error.status === 404) return { title: "Not found", message: "That item could not be found. It may have been removed." };
    return { title: "Something went wrong", message: error.message };
  }
  if (error instanceof Error) return { title: "Something went wrong", message: error.message || "An unexpected error occurred." };
  return { title: "Something went wrong", message: "An unexpected error occurred." };
}

export function RouteErrorPage({ error, reset }: RouteErrorPageProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { title, message } = describe(error);
  const details = error instanceof Error ? `${error.name}: ${error.message}\n${error.stack ?? ""}` : JSON.stringify(error, null, 2);

  const retry = () => {
    reset?.();
    void router.invalidate();
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4">
        <div className="space-y-3 text-center">
          <div className="mx-auto grid size-12 place-items-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="size-6" />
          </div>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>
          <div className="flex justify-center gap-2">
            <Button onClick={retry}>
              <RefreshCw /> Try again
            </Button>
            <Button variant="outline" asChild>
              <Link to="/">Go to home</Link>
            </Button>
          </div>
        </div>
        <div className="surface overflow-hidden">
          <button type="button" className="flex w-full items-center justify-between px-4 py-2.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            Technical details
            <ChevronDown className={cn("size-4 transition-transform duration-150", open && "rotate-180")} />
          </button>
          {open && (
            <div className="relative border-t bg-muted/40">
              <CopyButton text={details} showLabel className="absolute top-2 right-2" />
              <pre className="mono max-h-64 overflow-auto p-4 text-xs whitespace-pre-wrap break-words text-muted-foreground">{details}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RouteErrorPage;
